import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import * as Sentry from '@sentry/browser';

import { SaveButton } from '../../../../../components/Button/ActionButtons';
import {
  Card,
  Container,
  HeaderContainer,
  SubTitle,
  Title,
  TitleContainer,
} from '../../../../../components/v2/Card';
import { StyledTextFieldContainer } from '../../../../../components/Inputs';
import InputLabel from '../../../../../components/v2/InputLabel';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import Product from '../../../../../Model/Product';
import AutoComplete from '../../../../components/AutoComplete';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import useToast from '../../../../hooks/useToast';
import { HelperText, UpdatedSettings, UpdatedSettingItem } from '../../styles';
import AppThemeFields, { colorModeLabel } from '../AppThemeFields';
import texts from './texts.json';

/**
 * A product's own app look, for the coach being configured. The mobile app shows it instead of the coach's to the
 * clients it loads that product for, whatever the state of their contract; a value left empty follows the coach's.
 */
const ProductAppTheme = ({ coachId, themeOptions }) => {
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [themeId, setThemeId] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [colorMode, setColorMode] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // The coach's products, read once: a save reads them again (see `handleSave`).
  const loadProducts = useCallback(async () => {
    try {
      const { docs } = await Product.getProductByCoachId(coachId);
      if (isComponentMountedRef.current) {
        setProducts(docs);
      }
      return docs;
    } catch (error) {
      Sentry.captureException(error, { extra: { coachId } });
      if (isComponentMountedRef.current) {
        showToast(texts.loadFailed, { error: true });
      }
      return [];
    }
  }, [coachId, isComponentMountedRef, showToast]);

  useEffect(() => {
    const init = async () => {
      await loadProducts();
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    };
    init();
  }, [loadProducts, isComponentMountedRef]);

  // Hide the "nothing to save" error once something changes.
  useEffect(() => {
    setShowErrors(false);
  }, [themeId, primaryColor, colorMode]);

  const productOptions = useMemo(() => products.map((doc) => ({
    id: doc.id,
    label: doc.isArchived ? format(texts.archivedProduct, { name: doc.name }) : doc.name,
  })), [products]);

  // The form starts from what the product has saved.
  const showProduct = (selected) => {
    setProduct(selected);
    setThemeId(selected?.appThemePreset || '');
    setPrimaryColor(selected?.appPrimaryColor || '');
    setColorMode(selected?.appColorMode || '');
    setShowErrors(false);
  };

  const changes = {
    theme: !!product && product.appThemePreset !== themeId,
    color: !!product && product.appPrimaryColor !== primaryColor,
    colorMode: !!product && product.appColorMode !== colorMode,
  };
  const themeLabel = themeOptions.find(({ id }) => id === themeId)?.label;
  const changeTexts = [
    changes.theme && format(texts.themeChange, { theme: themeLabel || texts.coachValue }),
    changes.color && format(texts.colorChange, { color: primaryColor || texts.coachValue }),
    changes.colorMode && format(texts.colorModeChange, { colorMode: colorModeLabel(colorMode) }),
  ].filter(Boolean);

  const handleSaveClick = () => {
    if (!changeTexts.length) {
      setShowErrors(true);
      return;
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    setShowModal(false);
    setIsProcessing(true);
    try {
      await product.setAppTheme({ colorMode, baseTheme: themeId, primaryColor });
      showToast(format(texts.saveSuccessful, { product: product.name }));
    } catch (error) {
      Sentry.captureException(error, { extra: { productId: product.id } });
      showToast(texts.saveFailed, { error: true });
      setIsProcessing(false);
      return;
    }
    // The products came from a one-off read, so the form starts again from what was saved.
    const docs = await loadProducts();
    if (isComponentMountedRef.current) {
      showProduct(docs.find(({ id }) => id === product.id) || null);
      setIsProcessing(false);
    }
  };

  return (
    <Card>
      <HeaderContainer>
        <TitleContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.description}</SubTitle>
        </TitleContainer>
      </HeaderContainer>
      <Container>
        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.product}</InputLabel>
          <AutoComplete
            options={productOptions}
            onChange={(option) => showProduct(products.find(({ id }) => id === option?.id) || null)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={isLoading}
          />
        </StyledTextFieldContainer>
        {!!product && (
          <>
            <AppThemeFields
              key={product.id}
              themeOptions={themeOptions}
              themeId={themeId}
              onThemeChange={(theme) => setThemeId(theme?.id || '')}
              primaryColor={primaryColor}
              onPrimaryColorChange={setPrimaryColor}
              colorMode={colorMode}
              onColorModeChange={setColorMode}
              followsCoach
            />
            {showErrors && <HelperText error>{texts.noChange}</HelperText>}
            <SaveButton onClick={handleSaveClick} $fitToContent>
              {texts.save}
            </SaveButton>
          </>
        )}
      </Container>
      <ConfirmDialog
        isOpen={showModal}
        onConfirm={handleSave}
        onCancel={() => setShowModal(false)}
        dialogTexts={{
          title: format(texts.confirmTitle, { product: product?.name }),
          content: (
            <UpdatedSettings>
              {changeTexts.map((text) => <UpdatedSettingItem key={text}>{text}</UpdatedSettingItem>)}
            </UpdatedSettings>
          ),
        }}
      />
      <LoadingOverlay isLoading={isProcessing} />
    </Card>
  );
};

ProductAppTheme.propTypes = {
  coachId: PropTypes.string.isRequired,
  themeOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })).isRequired,
};

export default ProductAppTheme;
